/**
 * M7 S6: online-only story countdowns (spec §10.4, §6 Okafor's risk, §8.2 floors; plan §2 "Online faction days").
 *
 * Every story countdown (`career.story.countdowns`) counts **online** faction days: `tickOnlineDay` runs once per
 * faction day the player was online and fires what fell due; `catchUpStory` runs for an absence and never advances
 * anything. Instead it collects the away-report warnings for countdowns that would have fallen due while the player
 * was away (Okafor is never taken while the player is away, EX-8), and holds floors that would have run out offline
 * until the player has been online `finale.json floorExtensionOnlineDays` days (spec §8.2).
 *
 * Built-in kinds: `retaliation` (S5's Asset Recovery hunt, `retaliation:hunt`): when it fires the worker gets the
 * new `setRetaliation` set without the hunt. `floor` countdowns carry their worker command in `ref`
 * (`floorRef.aggression`, `floorRef.grievance`), so the floor is re-sent when it moves and on re-apply. Other kinds
 * (callbacks, Okafor's steps, the surcharge) are handed to the caller's `handlers` and listed in `fired`.
 *
 * Pure (Career in, Career out); event-driven, never per frame.
 */
import { z } from 'zod'
import type { Career } from '../career/types'
import { logger } from '../core/logger'
import runtimeJson from '../data/story/runtime.json'
import { FACTION_IDS, type FactionId } from '../strategic/types'
import { activeRetaliationRows, HUNT_COUNTDOWN_ID } from './leverage'
import { storyOf, withStory } from './state'
import {
  COUNTDOWN_KINDS,
  type CountdownKind,
  type StoryCommand,
  type StoryCountdown,
  type StoryEffect,
  type StoryParty,
  type StoryResult,
  type StoryState,
} from './types'

// ---------------------------------------------------------------------------------------------
// runtime.json
// ---------------------------------------------------------------------------------------------

export const runtimeFileSchema = z.strictObject({
  comment: z.string(),
  extendOnReturn: z.array(z.enum(COUNTDOWN_KINDS)).default(() => []),
  awayWarnings: z.partialRecord(z.enum(COUNTDOWN_KINDS), z.string().min(1)).default(() => ({})),
})
export type RuntimeConfig = z.output<typeof runtimeFileSchema>

/** Parses a `runtime.json` (tests pass their own); throws with the path on a bad file. */
export function loadRuntimeConfig(raw: unknown): RuntimeConfig {
  const result = runtimeFileSchema.safeParse(raw)
  if (result.success) return result.data
  const issue = result.error.issues[0]
  const where = issue && issue.path.length > 0 ? issue.path.map(String).join('.') : '(root)'
  throw new Error(`story data: runtime.json: ${where}: ${issue?.message ?? 'invalid'}`)
}

let cached: RuntimeConfig | undefined

/** The shipped `runtime.json`, parsed once on first use. */
export function runtimeConfig(): RuntimeConfig {
  cached ??= loadRuntimeConfig(runtimeJson)
  return cached
}

// ---------------------------------------------------------------------------------------------
// Scheduling
// ---------------------------------------------------------------------------------------------

export interface CountdownSpec {
  id: string
  kind: CountdownKind
  /** Online days from now (0 = fires on the next online day). */
  inOnlineDays: number
  ref?: string
}

/** Order: due day, then id (deterministic firing and re-apply). */
function byDue(a: StoryCountdown, b: StoryCountdown): number {
  return a.dueOnlineDay - b.dueOnlineDay || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)
}

export function countdownOf(career: Career, id: string): StoryCountdown | undefined {
  return storyOf(career).countdowns.find((c) => c.id === id)
}

/** Schedules (or replaces, by id) a countdown due `inOnlineDays` online days from now. */
export function scheduleCountdown(career: Career, spec: CountdownSpec): Career {
  if (!Number.isInteger(spec.inOnlineDays) || spec.inOnlineDays < 0) {
    throw new RangeError(`scheduleCountdown: inOnlineDays ${spec.inOnlineDays} is not a whole number ≥ 0`)
  }
  return withStory(career, (s) => {
    const cd: StoryCountdown = {
      id: spec.id,
      kind: spec.kind,
      startOnlineDay: s.onlineDays,
      dueOnlineDay: s.onlineDays + spec.inOnlineDays,
    }
    if (spec.ref !== undefined) cd.ref = spec.ref
    return { ...s, countdowns: [...s.countdowns.filter((c) => c.id !== spec.id), cd] }
  })
}

/** Drops a countdown (the same career when there is none). */
export function cancelCountdown(career: Career, id: string): Career {
  if (countdownOf(career, id) === undefined) return career
  return withStory(career, (s) => ({ ...s, countdowns: s.countdowns.filter((c) => c.id !== id) }))
}

// ---------------------------------------------------------------------------------------------
// Floors (spec §8.2): the worker command rides in the countdown's `ref`.
// ---------------------------------------------------------------------------------------------

const FACTIONS: ReadonlySet<string> = new Set(FACTION_IDS)

function isParty(v: string): v is StoryParty {
  return v === 'player' || FACTIONS.has(v)
}

function isFaction(v: string): v is FactionId {
  return FACTIONS.has(v)
}

/** `floor` countdown refs (S21 schedules them; the runtime re-sends them). */
export const floorRef = {
  aggression: (faction: FactionId, value: number): string => `aggression:${faction}:${value}`,
  grievance: (a: StoryParty, b: StoryParty, floor: number): string => `grievance:${a}:${b}:${floor}`,
} as const

/** The worker command a `floor` countdown stands for, or null for a malformed ref (logged). */
export function floorCommand(cd: StoryCountdown): StoryCommand | null {
  const parts = (cd.ref ?? '').split(':')
  if (parts[0] === 'aggression' && parts.length === 3) {
    const value = Number(parts[2])
    if (isFaction(parts[1]) && Number.isFinite(value)) {
      return { type: 'setAggressionFloor', faction: parts[1], value, untilOnlineDay: cd.dueOnlineDay }
    }
  } else if (parts[0] === 'grievance' && parts.length === 4) {
    const floor = Number(parts[3])
    if (isParty(parts[1]) && isParty(parts[2]) && Number.isFinite(floor)) {
      return { type: 'setGrievance', a: parts[1], b: parts[2], floor, untilOnlineDay: cd.dueOnlineDay }
    }
  }
  logger.warn(`story: floor countdown ${cd.id} has no usable ref '${cd.ref ?? ''}'`)
  return null
}

/** Every live floor's command, in due order (re-apply, and after a floor moves). */
export function floorCommands(story: StoryState): StoryCommand[] {
  const out: StoryCommand[] = []
  for (const cd of [...story.countdowns].sort(byDue)) {
    if (cd.kind !== 'floor' || cd.dueOnlineDay <= story.onlineDays) continue
    const cmd = floorCommand(cd)
    if (cmd !== null) out.push(cmd)
  }
  return out
}

// ---------------------------------------------------------------------------------------------
// The online day
// ---------------------------------------------------------------------------------------------

/** A handler for a fired countdown kind (S11 callbacks, S13 Okafor, S21 surcharge). */
export type CountdownHandler = (career: Career, fired: StoryCountdown) => StoryResult<Career>

export interface TickOptions {
  handlers?: Partial<Record<CountdownKind, CountdownHandler>>
}

export interface TickResult extends StoryResult<Career> {
  /** Countdowns that fell due this online day, in due order (removed from the career). */
  fired: StoryCountdown[]
}

/**
 * One online faction day (plan §2: the caller calls it only for a day not simulated by catch-up): counts it, then
 * fires every countdown now due, in due order, through the built-ins and `handlers`. Fired countdowns are removed
 * before their handler runs, so a handler can schedule a follow-up with the same id.
 */
export function tickOnlineDay(career: Career, opts: TickOptions = {}): TickResult {
  let next = withStory(career, (s) => ({ ...s, onlineDays: s.onlineDays + 1 }))
  const story = storyOf(next)
  const fired = story.countdowns.filter((c) => c.dueOnlineDay <= story.onlineDays).sort(byDue)
  const commands: StoryCommand[] = []
  const effects: StoryEffect[] = []
  if (fired.length === 0) return { career: next, commands, effects, fired }

  const firedIds = new Set(fired.map((c) => c.id))
  next = withStory(next, (s) => ({ ...s, countdowns: s.countdowns.filter((c) => !firedIds.has(c.id)) }))
  for (const cd of fired) {
    if (cd.kind === 'retaliation' && cd.id === HUNT_COUNTDOWN_ID) {
      commands.push({ type: 'setRetaliation', faction: 'tethys', rows: activeRetaliationRows(storyOf(next)) })
    }
    const handler = opts.handlers?.[cd.kind]
    if (handler === undefined) continue
    const r = handler(next, cd)
    next = r.career
    commands.push(...r.commands)
    effects.push(...r.effects)
  }
  return { career: next, commands, effects, fired }
}

// ---------------------------------------------------------------------------------------------
// The absence
// ---------------------------------------------------------------------------------------------

export interface CatchUpData {
  runtime: RuntimeConfig
  /** `finale.json floorExtensionOnlineDays`. */
  floorExtensionOnlineDays: number
}

export interface CatchUpStoryResult extends StoryResult<Career> {
  /** The away-report lines (also emitted as `awayWarning` effects), one per distinct line, in due order. */
  warnings: string[]
}

/**
 * An absence of `simulatedDays` faction days (`AwayReport.simulatedDays`): story time doesn't move (no online day
 * counts, nothing fires). A countdown that would have fallen due within the absence gives its kind's away warning
 * (spec §6, T18); one of an `extendOnReturn` kind holds until the player has been online
 * `floorExtensionOnlineDays` days (spec §8.2), and a moved floor is re-sent to the worker.
 */
export function catchUpStory(career: Career, simulatedDays: number, data: CatchUpData): CatchUpStoryResult {
  const story = storyOf(career)
  const commands: StoryCommand[] = []
  const effects: StoryEffect[] = []
  const warnings: string[] = []
  if (simulatedDays <= 0 || story.countdowns.length === 0) return { career, commands, effects, warnings }

  const extend: ReadonlySet<CountdownKind> = new Set(data.runtime.extendOnReturn)
  const holdUntil = story.onlineDays + data.floorExtensionOnlineDays
  let moved = false
  const countdowns = story.countdowns.map((cd) => {
    const wouldFall = cd.dueOnlineDay - story.onlineDays <= simulatedDays
    if (!wouldFall || !extend.has(cd.kind) || cd.dueOnlineDay >= holdUntil) return cd
    moved = true
    return { ...cd, dueOnlineDay: holdUntil }
  })
  for (const cd of [...story.countdowns].sort(byDue)) {
    if (cd.dueOnlineDay - story.onlineDays > simulatedDays) continue
    const text = data.runtime.awayWarnings[cd.kind]
    if (text === undefined || warnings.includes(text)) continue
    warnings.push(text)
    effects.push({ type: 'awayWarning', text })
  }
  if (!moved) return { career, commands, effects, warnings }

  const nextStory: StoryState = { ...story, countdowns }
  for (const cd of countdowns) {
    if (cd.kind !== 'floor' || story.countdowns.find((c) => c.id === cd.id)?.dueOnlineDay === cd.dueOnlineDay) continue
    const cmd = floorCommand(cd)
    if (cmd !== null) commands.push(cmd)
  }
  return { career: { ...career, story: nextStory }, commands, effects, warnings }
}
